import React, { useEffect, useState } from 'react';
import { BarChart3, TrendingUp, Users, Award } from 'lucide-react';
import { analyticsAPI } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';

const AdminAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [topServices, setTopServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [dashRes, servicesRes] = await Promise.all([
        analyticsAPI.getDashboard(),
        analyticsAPI.getServices()
      ]);
      setStats(dashRes.data);
      setTopServices(servicesRes.data.top_services || []);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 pt-12 lg:pt-0">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <div key={i} className="skeleton h-28 rounded-2xl" />)}
        </div>
        <div className="skeleton h-80 rounded-2xl" />
      </div>
    );
  }

  const tierData = stats?.tier_distribution ? [
    { name: 'Bronze', count: stats.tier_distribution.Bronze || 0 },
    { name: 'Silver', count: stats.tier_distribution.Silver || 0 },
    { name: 'Gold', count: stats.tier_distribution.Gold || 0 },
    { name: 'Platinum', count: stats.tier_distribution.Platinum || 0 },
  ] : [];

  return (
    <div className="space-y-6 pt-12 lg:pt-0 animate-fadeIn">
      <div>
        <h1 className="font-serif text-3xl text-[#4A154B]">Analytics</h1>
        <p className="text-gray-600">Detailed insights and performance metrics</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass rounded-2xl p-5">
          <div className="w-10 h-10 bg-[#4A154B] rounded-xl flex items-center justify-center mb-3">
            <Users className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm text-gray-500">Total Customers</p>
          <p className="font-serif text-2xl text-[#4A154B]">{stats?.total_customers || 0}</p>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="w-10 h-10 bg-[#C9A689] rounded-xl flex items-center justify-center mb-3">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm text-gray-500">Today's Revenue</p>
          <p className="font-serif text-2xl text-[#4A154B]">${(stats?.today?.revenue || 0).toFixed(0)}</p>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="w-10 h-10 bg-[#8FBC8F] rounded-xl flex items-center justify-center mb-3">
            <Award className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm text-gray-500">Points Awarded Today</p>
          <p className="font-serif text-2xl text-[#4A154B]">{stats?.today?.points_awarded || 0}</p>
        </div>
        <div className="glass rounded-2xl p-5">
          <div className="w-10 h-10 bg-[#2C2C2C] rounded-xl flex items-center justify-center mb-3">
            <BarChart3 className="w-5 h-5 text-white" />
          </div>
          <p className="text-sm text-gray-500">Today's Visits</p>
          <p className="font-serif text-2xl text-[#4A154B]">{stats?.today?.customer_visits || 0}</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Points Trend */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif text-xl text-[#4A154B] mb-4">Points Activity (30 Days)</h3>
          {stats?.points_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={stats.points_trend}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Line type="monotone" dataKey="earned" stroke="#8FBC8F" strokeWidth={2} dot={false} name="Earned" />
                <Line type="monotone" dataKey="redeemed" stroke="#C9A689" strokeWidth={2} dot={false} name="Redeemed" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-12">No data available</p>
          )}
        </div>

        {/* Tier Distribution */}
        <div className="glass rounded-2xl p-6">
          <h3 className="font-serif text-xl text-[#4A154B] mb-4">Customer Tiers</h3>
          {tierData.some(d => d.count > 0) ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={tierData} layout="vertical">
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 12 }} width={70} />
                <Tooltip />
                <Bar dataKey="count" fill="#C9A689" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-gray-500 text-center py-12">No customer data</p>
          )}
        </div>
      </div>

      {/* Top Services */}
      <div className="glass rounded-2xl p-6">
        <h3 className="font-serif text-xl text-[#4A154B] mb-4">Top Services</h3>
        {topServices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#F5E6D3]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-500">Service</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Transactions</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-500">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {topServices.map((service, index) => (
                  <tr key={index} className="border-b border-[#F5E6D3]/50 last:border-0">
                    <td className="py-3 px-4 text-[#4A154B] font-medium">{service.name}</td>
                    <td className="py-3 px-4 text-right text-gray-600">{service.count}</td>
                    <td className="py-3 px-4 text-right font-medium text-[#C9A689]">${service.revenue?.toFixed(0) || 0}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-500 text-center py-8">No service data available</p>
        )}
      </div>
    </div>
  );
};

export default AdminAnalytics;
